import { Action, action, Thunk, thunk } from 'easy-peasy'
import { WS_GATEWAY_URL } from '../env'
import ReconnectingWebSocket, { Options } from '../socket/ReconnectingWebsocket'
import store, { StoreModel } from '../store/store'
import { decode, Decoders, encode, SendMessageArgs } from './grpcHelpers'

export type MessageTypeToCallbacks = {
    [K in keyof Decoders]: {
        messageType: K
        callback: (message: ReturnType<Decoders[K]['decode']>) => void
    }
}
export type Callback = MessageTypeToCallbacks[keyof Decoders]

// Define the WebSocket model interface
export type WebSocketModel = {
    // State
    socket: ReconnectingWebSocket | null
    messages: string[]
    isConnected: boolean
    healthCheckInterval: NodeJS.Timeout | undefined
    listeners: {
        [K in keyof Decoders]?: MessageTypeToCallbacks[K]['callback']
    }

    // Actions
    setSocket: Action<WebSocketModel, ReconnectingWebSocket | null>
    setConnected: Action<WebSocketModel, boolean>
    setHealthCheckInterval: Action<WebSocketModel, NodeJS.Timeout | undefined>
    addListener: Action<WebSocketModel, Callback>

    // Thunks
    connect: Thunk<WebSocketModel, undefined, undefined, StoreModel>
    sendMessage: Thunk<WebSocketModel, SendMessageArgs, {}, WebSocketModel>
    disconnect: Thunk<WebSocketModel>
}

// WebSocket model implementation
export const WebsocketModel: WebSocketModel = {
    // State
    socket: null,
    messages: [],
    isConnected: false,
    healthCheckInterval: undefined,

    listeners: {},

    // Actions
    setSocket: action((state, socket) => {
        state.socket = socket
    }),

    setConnected: action((state, isConnected) => {
        state.isConnected = isConnected
    }),

    setHealthCheckInterval: action((state, interval) => {
        state.healthCheckInterval = interval
    }),

    addListener: action((state, { messageType, callback }) => {
        state.listeners[messageType] = callback as any
    }),

    // Thunks
    connect: thunk((actions, _, { getState, getStoreState }) => {
        const token = getStoreState().token

        if (!token) {
            actions.setConnected(false)
            actions.setSocket(null)
            return
        }

        // Clean up existing connection if any
        const existingSocket = getState().socket
        if (existingSocket) {
            console.log('[WS-GATEWAY] Closing existing socket')
            existingSocket.close()
        }
        const existingInterval = getState().healthCheckInterval
        if (existingInterval) {
            console.log('[WS-GATEWAY] Clearing existing interval in connect (ID:', existingInterval, ')')
            clearInterval(existingInterval)
            actions.setHealthCheckInterval(undefined)
        }

        // A URL-provider function (not a frozen string) so the class's own internal
        // reconnect — triggered by a dropped connection or appResume, not by this
        // thunk running again — re-reads the current token instead of replaying
        // whatever token was live when this socket was first constructed.
        const url = () => `${WS_GATEWAY_URL}/api/ws?token=${getStoreState().token}`
        const options: Options = {
            debug: true,
            debugName: 'WS-GATEWAY',
            // Moderate reconnection for gateway (important but not as critical as active game)
            minReconnectionDelay: 1000,
            maxReconnectionDelay: 60000,
            reconnectionDelayGrowFactor: 2.0,
            // 30 retries over ~30 minutes
            maxRetries: 30,
            minUptime: 10000,
            connectionTimeout: 8000, // TODO: Consider increasing for mobile after backend coordination
            // IMPORTANT: Disable built-in keepalive!
            // Gateway uses gRPC binary protocol, not JSON keepalive
            // We send custom binary health checks (empty Uint8Array) instead
            keepaliveDelay: null,
            useJitter: true,
        }

        const socket = new ReconnectingWebSocket(url, undefined, options)
        socket.binaryType = 'arraybuffer'

        socket.addEventListener('open', () => {
            console.log('[WS-GATEWAY] open event - connected', socket.readyState)
            actions.setConnected(true)

            // Clear any existing health check interval before creating a new one
            const existingInterval = getState().healthCheckInterval
            if (existingInterval) {
                console.log('[WS-GATEWAY] Clearing existing interval on open')
                clearInterval(existingInterval)
            }

            // Setup health check interval (send empty message every 5 seconds)
            const healthCheckInterval = setInterval(() => {
                console.log('[WS-GATEWAY] Health check tick - readyState:', socket.readyState, 'OPEN =', socket.OPEN)
                if (socket.readyState === socket.OPEN) {
                    console.log('[WS-GATEWAY] Sending health check keepalive')
                    socket.send(new Uint8Array())
                } else {
                    console.log('[WS-GATEWAY] Skipping keepalive - socket not open')
                }
            }, 5000)
            console.log('[WS-GATEWAY] Health check interval created (ID:', healthCheckInterval, ')')
            actions.setHealthCheckInterval(healthCheckInterval)

            // Establish listeners
            store.getActions().popupService.establishListener()
            store.getActions().missionsService.establishListener()
            store.getActions().dailyPuzzleView.establishListener()
            store.getActions().dailyPuzzleViewV2.establishListener()
        })

        socket.addEventListener('reconnect', () => {
            console.log('[WS-GATEWAY] reconnect event - reconnected', socket.readyState)
            actions.setConnected(true)

            // Clear any existing health check interval before creating a new one
            const existingInterval = getState().healthCheckInterval
            if (existingInterval) {
                console.log('[WS-GATEWAY] Clearing existing interval on reconnect (ID:', existingInterval, ')')
                clearInterval(existingInterval)
            }

            // Setup health check interval (send empty message every 5 seconds)
            const healthCheckInterval = setInterval(() => {
                console.log('[WS-GATEWAY] Health check tick (reconnected) - readyState:', socket.readyState, 'OPEN =', socket.OPEN)
                if (socket.readyState === socket.OPEN) {
                    console.log('[WS-GATEWAY] Sending health check keepalive (reconnected)')
                    socket.send(new Uint8Array())
                } else {
                    console.log('[WS-GATEWAY] Skipping keepalive - socket not open (reconnected)')
                }
            }, 5000)
            console.log('[WS-GATEWAY] Health check interval created on reconnect (ID:', healthCheckInterval, ')')
            actions.setHealthCheckInterval(healthCheckInterval)

            // Re-establish listeners after reconnect
            store.getActions().popupService.establishListener()
            store.getActions().missionsService.establishListener()
            store.getActions().dailyPuzzleView.establishListener()
            store.getActions().dailyPuzzleViewV2.establishListener()
        })

        socket.addEventListener('message', (event) => {
            // Skip empty messages (health check responses)
            if (event.data.byteLength === 0) {
                console.log('[WS-GATEWAY] Received health check response (empty message)')
                return
            }

            const { messageType, message } = decode(event.data)

            const listener = getState().listeners[messageType]
            if (listener) {
                listener(message as any)
            } else {
                console.warn(`No listener for message type: ${messageType}`)
            }
        })

        socket.addEventListener('close', () => {
            console.log('[WS-GATEWAY] close event - disconnected')
            actions.setConnected(false)

            // Clear health check interval
            const interval = getState().healthCheckInterval
            if (interval) {
                console.log('[WS-GATEWAY] Clearing health check interval on close (ID:', interval, ')')
                clearInterval(interval)
                actions.setHealthCheckInterval(undefined)
            } else {
                console.log('[WS-GATEWAY] No health check interval to clear on close')
            }
        })

        socket.addEventListener('error', (error) => {
            console.error('WebSocket gateway error:', error.message)
        })

        actions.setSocket(socket)
    }),

    sendMessage: thunk((actions, { messageType, message }, { getState }) => {
        const { socket, isConnected } = getState()
        if (socket && isConnected && socket.readyState === socket.OPEN) {
            const encodedMessage = encode(messageType, message)
            socket.send(encodedMessage)
        } else {
            console.error('Cannot send message: WebSocket gateway is not connected')
        }
    }),

    disconnect: thunk((actions, _, { getState }) => {
        const { socket, healthCheckInterval } = getState()

        // Clear health check interval
        if (healthCheckInterval) {
            clearInterval(healthCheckInterval)
            actions.setHealthCheckInterval(undefined)
        }

        if (socket) {
            socket.close()
            actions.setSocket(null)
        }

        actions.setConnected(false)
    }),
}
